/**
 * InstallPromptContext — Landlord
 * Captures the browser's beforeinstallprompt event and exposes
 * install prompt, banner visibility, and dismiss logic.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';

const COOLDOWN_KEY = 'pwa_install_banner_dismissed_at';
const COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallPromptContextValue {
  isInstallable: boolean;
  shouldShowBanner: boolean;
  promptInstall: () => Promise<void>;
  dismissBanner: () => void;
}

const InstallPromptContext = createContext<InstallPromptContextValue | undefined>(undefined);

// True when the app is already running as an installed PWA (mobile home-screen
// or desktop standalone window) — installable/prompt logic below excludes this
// case since there's nothing left to install.
const isInStandaloneMode =
  window.matchMedia('(display-mode: standalone)').matches ||
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (navigator as any).standalone === true;

export function InstallPromptProvider({ children }: { children: ReactNode }) {
  const [deferredEvent, setDeferredEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    const ts = localStorage.getItem(COOLDOWN_KEY);
    return ts ? Date.now() - Number(ts) < COOLDOWN_MS : false;
  });

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredEvent(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const isInstallable = deferredEvent !== null;
  // Banner shows on mobile and desktop alike, when installable (not already
  // running standalone) and the 30-day cooldown hasn't been triggered.
  const shouldShowBanner = isInstallable && !isInStandaloneMode && !bannerDismissed;

  const promptInstall = useCallback(async () => {
    if (!deferredEvent) return;
    await deferredEvent.prompt();
    const { outcome } = await deferredEvent.userChoice;
    if (outcome === 'accepted') {
      setDeferredEvent(null);
    }
  }, [deferredEvent]);

  const dismissBanner = useCallback(() => {
    localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
    setBannerDismissed(true);
  }, []);

  return (
    <InstallPromptContext.Provider value={{ isInstallable, shouldShowBanner, promptInstall, dismissBanner }}>
      {children}
    </InstallPromptContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useInstallPrompt(): InstallPromptContextValue {
  const ctx = useContext(InstallPromptContext);
  if (!ctx) throw new Error('useInstallPrompt must be used within InstallPromptProvider');
  return ctx;
}
